import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CATEGORY_ICONS } from '../lib/categoryIcons';
import { fetchCategories, preloaded } from '../lib/api';
import { imageProps } from '../lib/images';

const FALLBACK_ICON = 'Package';
const slugToFallbackIcon = {
  exaustores: 'Wind',
  kits: 'Package',
  churrasqueira: 'Beef',
  cooktops: 'ChefHat',
  pecas: 'Wrench',
  fogoes: 'Flame',
  grades: 'LayoutGrid',
};

const isImageIcon = (cat) => {
  if (cat.icon_type === 'image') return true;
  const v = cat.icon || '';
  return v.startsWith('data:image') || v.startsWith('http') || v.startsWith('/');
};

const renderCategoryVisual = (cat) => {
  if (isImageIcon(cat) && cat.icon) {
    return (
      <img
        {...imageProps(cat.icon, { sizes: '120px' })}
        alt={cat.name}
        loading="lazy"
        decoding="async"
        width="120"
        height="120"
        className="max-w-[80%] max-h-[80%] object-contain transition-transform duration-500 group-hover:scale-110"
      />
    );
  }
  const name = cat.icon || slugToFallbackIcon[cat.id] || FALLBACK_ICON;
  const IconComp = CATEGORY_ICONS[name] || CATEGORY_ICONS[FALLBACK_ICON];
  return <IconComp size={40} className="text-blue-700 transition-transform duration-500 group-hover:scale-110" strokeWidth={2.2} />;
};

const ITEMS_PER_PAGE = 5;

const CategoriesSlider = () => {
  const [categories, setCategories] = useState(() => preloaded('categories') || []);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(() => !preloaded('categories'));

  useEffect(() => {
    fetchCategories()
      .then((data) => { if (Array.isArray(data)) setCategories(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totalPages = Math.max(1, Math.ceil(categories.length / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const visible = useMemo(
    () => categories.slice(safePage * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE + ITEMS_PER_PAGE),
    [categories, safePage]
  );

  const goPrev = () => setPage((p) => (p - 1 + totalPages) % totalPages);
  const goNext = () => setPage((p) => (p + 1) % totalPages);

  if (loading || categories.length === 0) return null;
  const showArrows = categories.length > ITEMS_PER_PAGE;

  return (
    <section className="bg-white py-12 md:py-16 relative z-10" data-testid="categories-slider">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-[11px] md:text-xs font-bold tracking-[0.2em] text-blue-700 mb-1">
              NAVEGUE POR
            </p>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 leading-tight">
              Principais Categorias
            </h2>
          </div>
          {showArrows && (
            <div className="flex items-center gap-2">
              <button
                onClick={goPrev}
                className="w-11 h-11 rounded-full bg-white border-2 border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white transition-all flex items-center justify-center shadow-sm hover:shadow-md active:scale-95"
                aria-label="Categorias anteriores"
                data-testid="categories-slider-prev"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={goNext}
                className="w-11 h-11 rounded-full bg-blue-700 text-white hover:bg-blue-800 transition-all flex items-center justify-center shadow-sm hover:shadow-md active:scale-95"
                aria-label="Próximas categorias"
                data-testid="categories-slider-next"
              >
                <ChevronRight size={22} />
              </button>
            </div>
          )}
        </div>

        <div
          key={safePage}
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-5 animate-slide-fade"
        >
          {visible.map((cat) => (
            <Link
              key={cat.id}
              to={`/categoria/${cat.id}`}
              data-testid={`category-slider-item-${cat.id}`}
              className="group relative bg-white rounded-2xl border border-gray-200 hover:border-blue-700 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            >
              {/* Image / Icon container — square */}
              <div className="aspect-square bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-5 relative overflow-hidden">
                {/* Subtle background accent */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-blue-50/60 to-transparent transition-opacity duration-500" />
                <div className="relative z-10 w-full h-full flex items-center justify-center">
                  {renderCategoryVisual(cat)}
                </div>
              </div>

              {/* Name strip */}
              <div className="px-3 py-3 md:py-4 text-center bg-white border-t border-gray-100">
                <span className="block text-xs md:text-sm font-bold text-gray-800 group-hover:text-blue-700 transition-colors leading-tight line-clamp-2">
                  {cat.name}
                </span>
              </div>

              {/* Yellow accent bar on hover */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-yellow-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </Link>
          ))}
        </div>

        {showArrows && (
          <div className="flex justify-center gap-2 mt-8" data-testid="categories-slider-dots">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                aria-label={`Página ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === safePage ? 'w-8 bg-blue-700' : 'w-2 bg-blue-200 hover:bg-blue-400'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes slide-fade {
          0% { opacity: 0; transform: translateX(20px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        .animate-slide-fade { animation: slide-fade 0.4s ease-out; }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
};

export default CategoriesSlider;
